/**
 * Typed fetch wrapper — equivalent to Refit/HttpClient in .NET or Feign in Spring.
 * All API calls go through here so auth, error handling, and base URL are centralised.
 */

const BASE = import.meta.env.VITE_API_BASE ?? ''

// AuthProvider calls this once on mount so every request carries the current token.
let _getToken: (() => string | null) | null = null
export function setTokenGetter(getter: () => string | null): void {
  _getToken = getter
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/** Base URL + auth header for callers that need the raw ``fetch`` (streams). */
export function apiBase(): string {
  return BASE
}

export function authHeaders(): Record<string, string> {
  const token = _getToken?.()
  return token ? { Authorization: `Bearer ${token}` } : {}
}

export async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const token = _getToken?.()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers['Authorization'] = `Bearer ${token}`
  if (init?.headers) {
    Object.assign(headers, init.headers)
  }
  const res = await fetch(`${BASE}${path}`, {
    headers,
    ...init,
  })
  if (!res.ok) {
    let message = res.statusText
    let body: unknown
    try {
      body = await res.json()
      if (typeof body === 'object' && body !== null && 'message' in body) {
        const maybeMessage = (body as { message?: unknown }).message
        if (typeof maybeMessage === 'string' && maybeMessage.trim()) {
          message = maybeMessage
        }
      }
    } catch {
      // ignore parse errors
    }
    throw new ApiError(res.status, message, body)
  }
  // 204 No Content
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export const api = {
  get<T>(path: string): Promise<T> {
    return request<T>(path)
  },
  post<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) })
  },
  delete<T>(path: string): Promise<T> {
    return request<T>(path, { method: 'DELETE' })
  },
  patch<T>(path: string, body?: unknown): Promise<T> {
    return request<T>(path, { method: 'PATCH', body: JSON.stringify(body ?? {}) })
  },
}

// ── Typed endpoint helpers ────────────────────────────────────────────────────

import type {
  AccountOption,
  AccountStatement,
  AccountSummary,
  AutoTradeStatus,
  AdaptersStatus,
  AuditByIdResponse,
  AuditHeatmap,
  AuditRecent,
  BacktestResult,
  DemoSettings,
  FundamentalsStatus,
  Overview,
  PrecheckStatus,
  ApplyTopNResult,
  ScreenerTopResponse,
  ScreenerHistoryResponse,
  ScreenerRunResponse,
  TopNBacktestResult,
  ShioajiSmoke,
  SignalFileInfo,
  SignalsContent,
  StrategyCatalogEntry,
  StrategyRunResponse,
  StrategyRecommendResponse,
  StrategyRecommendWatchlistMatrixResponse,
  StrategyPreviewBundle,
  TrainingCompare,
  TrainingAutotuneReport,
  TrainingHistory,
  TrainingLogs,
  TrainingRuntimeSettings,
  TrainingStatus,
  SentimentOverview,
  TwSentimentOverview,
  FearGreedData,
  NewsSentiment,
  EquityCurvePoint,
  ExperimentMatrix,
} from './types'

export interface StrategyPerformanceRow {
  /** Backend-computed (paper_runtime dispatch); groups the board into 股票/期貨. */
  asset_type?: 'stock' | 'futures' | string
  account_id: string
  strategy: string
  symbol: string
  unrealized_pnl: number | null
  pnl_7d: number
  pnl_30d: number
  pnl_90d: number
  trade_count_7d: number
  trade_count_30d: number
  trade_count_90d: number
  win_rate: number | null
  avg_win: number | null
  avg_loss: number | null
  last_trade: { executed_at: string; side: string; quantity: number; price: number } | null
  /** Account's since-inception cumulative return % — directly comparable to
   * benchmark_return_pct. null when unavailable OR the account holds more
   * than one (strategy, symbol) leg (attribution would be ambiguous). */
  account_return_pct: number | null
  /** Account's % return over the SAME trailing benchmark_window_days as
   * benchmark_return_pct / backtest_window_return_pct — derived from its
   * daily equity-curve snapshots (so it already includes unrealized P&L).
   * This is the number 回測對照's divergence flag is actually based on
   * (2026-07-15 alignment fix); null under the same conditions as
   * account_return_pct, or when there's no equity-curve point old enough to
   * anchor the window. */
  account_window_return_pct: number | null
  benchmark_window_days: number
  benchmark_return_pct: number | null
  /** Backtest's total return % over its FULL lookback (~120 days, fixed
   * NT$1,000,000 walk-forward) — supplementary context, NOT what divergence
   * is computed from. */
  backtest_return_pct?: number | null
  /** Backtest's % return over the SAME trailing benchmark_window_days as
   * account_window_return_pct, derived from its stored equity curve — this
   * is the number divergence actually compares against the live side. */
  backtest_window_return_pct?: number | null
  backtest_sharpe?: number | null
  backtest_win_rate?: number | null
  backtest_run_date?: string | null
  divergence: boolean | null
  /** Concrete, data-backed reasons for the divergence (server-computed —
   * never invented client-side). null unless divergence is true. */
  divergence_reasons?: string[] | null
}

export const accountsApi = {
  list: () => api.get<AccountSummary[]>('/api/accounts'),
  /** Row-level picker list (id / name / cash / asset_type) — use this, not
   *  list(), when the page only needs to choose an account. */
  options: () => api.get<AccountOption[]>('/api/accounts/options'),
  strategyPerformance: (accountId?: string) =>
    api.get<{
      rows: StrategyPerformanceRow[] | null
      equity_curves?: Record<string, EquityCurvePoint[]>
      generated_at: string
    }>(
      `/api/accounts/strategy-performance${accountId ? `?account_id=${accountId}` : ''}`),
  summary: (id: string) => api.get<AccountSummary>(`/api/accounts/${id}`),
  statement: (id: string) => api.get<AccountStatement>(`/api/accounts/${id}/statement`),
  refreshMarket: (id: string) => api.post(`/api/accounts/${id}/refresh-market`),
  /** Display label / hover description only — the id is the key and stays. */
  updateLabels: (id: string, body: { account_name?: string; description?: string }) =>
    api.patch<{ account_id: string; account_name?: string; description?: string | null }>(`/api/accounts/${id}`, body),
  delete: (id: string) => api.delete(`/api/accounts/${id}`),
  statementCsvUrl: (id: string, section = 'ledger') =>
    `${BASE}/api/accounts/${id}/statement.csv?section=${section}`,
}

export const paperApi = {
  createAccount: (body: Record<string, unknown>) => api.post('/api/paper/accounts', body),
  order: (body: Record<string, unknown>) => api.post('/api/paper/orders', body),
  mark: (body: Record<string, unknown>) => api.post('/api/paper/mark', body),
}

export const trainingApi = {
  status: () => api.get<TrainingStatus>('/api/training/status'),
  history: (limit = 20) => api.get<TrainingHistory>(`/api/training/history?limit=${limit}`),
  historyCsvUrl: () => `${BASE}/api/training/history.csv`,
  logs: (runId?: string, tail = 200) => {
    const params = new URLSearchParams()
    params.set('tail', String(tail))
    if (runId) params.set('run_id', runId)
    return api.get<TrainingLogs>(`/api/training/logs?${params.toString()}`)
  },
  compare: (a: string, b: string) => {
    const params = new URLSearchParams()
    params.set('run_id_a', a)
    params.set('run_id_b', b)
    return api.get<TrainingCompare>(`/api/training/compare?${params.toString()}`)
  },
  run: () => api.post<{ message: string }>('/api/training/run'),
  autotuneReport: () => api.get<TrainingAutotuneReport>('/api/training/autotune-report'),
  demoSettings: () => api.get<DemoSettings>('/api/training/demo-settings'),
  demoDefaults: (kind: string) =>
    api.get<{ demo: Record<string, unknown> }>(`/api/training/demo-defaults?strategy_kind=${kind}`),
  saveDemoSettings: (body: Record<string, unknown>) =>
    api.post('/api/training/demo-settings', body),
  runtimeSettings: () => api.get<TrainingRuntimeSettings>('/api/training/runtime-settings'),
  saveRuntimeSettings: (body: Record<string, unknown>) =>
    api.post('/api/training/runtime-settings', body),
  pruneLogs: (keep: number) => api.post('/api/training/logs/prune', { keep }),
  datasetInfo: () => api.get<{
    exists: boolean; path: string; size_bytes?: number; row_count?: number;
    symbol_count?: number; symbols?: string[]; start_date?: string; end_date?: string; mtime?: number;
  }>('/api/training/dataset-info'),
  refreshDataset: (body?: { symbols?: string[]; start_date?: string; end_date?: string; asset_type?: string }) =>
    api.post<{ ok: boolean; asset_type?: string; export_summary: Record<string, unknown> }>('/api/training/refresh-dataset', body),
  /** Merge OHLCV into price_dataset DB (config + watchlist stocks + optional extra_symbols). */
  refreshPriceIncremental: (body?: { extra_symbols?: string[] | string }) =>
    api.post<{
      ok: boolean
      returncode?: number
      stdout_tail?: string
      stderr_tail?: string | null
      message?: string
    }>('/api/training/refresh-price-incremental', body ?? {}),
  syncNow: () => api.post<{ message: string; started_at?: string; up_to_date?: boolean; price_date?: string; signal_at?: string }>('/api/training/sync-now'),
  syncStatus: () => api.get<{
    running: boolean
    last_start: string | null
    last_result: string | null
    log_tail: string[]
  }>('/api/training/sync-status'),
}

export const backtestApi = {
  run: (body: Record<string, unknown>) => api.post<BacktestResult>('/api/backtest/run', body),
}

export const screenerApi = {
  top: (limit = 20, date?: string) => {
    const params = new URLSearchParams({ limit: String(limit) })
    if (date) params.set('date', date)
    return api.get<ScreenerTopResponse>(`/api/screener/top?${params.toString()}`)
  },
  history: (tic: string, limit = 60) =>
    api.get<ScreenerHistoryResponse>(
      `/api/screener/history?tic=${encodeURIComponent(tic)}&limit=${limit}`,
    ),
  run: () => api.post<ScreenerRunResponse>('/api/screener/run'),
  applyTopN: (body: Record<string, unknown>) =>
    api.post<ApplyTopNResult>('/api/screener/apply-topn', body),
}

export const topNApi = {
  universe: () => api.get<{ symbols: string[]; count: number }>('/api/backtest/topn/universe'),
  backtest: (body: Record<string, unknown>) =>
    api.post<TopNBacktestResult>('/api/backtest/topn', body),
}

export const sentimentApi = {
  overview: () => api.get<SentimentOverview>('/api/sentiment/overview'),
  twOverview: () => api.get<TwSentimentOverview>('/api/sentiment/tw-overview'),
  fearGreed: () => api.get<FearGreedData>('/api/sentiment/fear-greed'),
  news: (ticker = 'SPY') =>
    api.get<NewsSentiment>(`/api/sentiment/news?ticker=${encodeURIComponent(ticker)}`),
}

export const strategiesApi = {
  catalog: () => api.get<StrategyCatalogEntry[]>('/api/strategies/catalog'),
  signalsList: () =>
    api.get<{ signals: SignalFileInfo[] }>('/api/strategy/signals-list'),
  preview: (body: Record<string, unknown>) =>
    api.post<StrategyPreviewBundle>('/api/strategies/preview', body),
  dryRun: (body: Record<string, unknown>) =>
    api.post<StrategyPreviewBundle>('/api/strategies/dry-run', body),
  runSignals: (body: Record<string, unknown>) =>
    api.post<StrategyRunResponse>('/api/strategy/run-signals', body),
  runSignalsDryRun: (body: Record<string, unknown>) =>
    api.post<StrategyRunResponse>('/api/strategy/run-signals/dry-run', body),
  recommend: (body: Record<string, unknown>) =>
    api.post<StrategyRecommendResponse>('/api/strategy/recommend', body),
  recommendWatchlistMatrix: (body: Record<string, unknown>) =>
    api.post<StrategyRecommendWatchlistMatrixResponse>(
      '/api/strategy/recommend-watchlist-matrix',
      body,
    ),
}

export const autoTradeApi = {
  start: (body: Record<string, unknown>) =>
    api.post<AutoTradeStatus>('/api/auto-trade/start', body),
  stop: () => api.post<AutoTradeStatus>('/api/auto-trade/stop'),
  status: () => api.get<AutoTradeStatus>('/api/auto-trade/status'),
}

export const overviewApi = {
  get: () => api.get<Overview>('/api/overview'),
}

export const fundamentalsApi = {
  status: () => api.get<FundamentalsStatus>('/api/fundamentals/status'),
  fetch: (body: Record<string, unknown>) => api.post('/api/fundamentals/fetch', body),
  merge: (body: Record<string, unknown>) => api.post('/api/fundamentals/merge', body),
}

// ── Audit / Ops / Signals / Shioaji (M2.1) ───────────────────────────────────

export const auditApi = {
  recent: (limit = 50, kind?: string) => {
    const params = new URLSearchParams()
    params.set('limit', String(limit))
    if (kind) params.set('kind', kind)
    return api.get<AuditRecent>(`/api/audit/recent?${params.toString()}`)
  },
  heatmap: (hours = 168) => api.get<AuditHeatmap>(`/api/audit/heatmap?hours=${hours}`),
  byId: (runId: string) =>
    api.get<AuditByIdResponse>(`/api/audit/by-id/${encodeURIComponent(runId)}`),
  exportCsvUrl: (limit = 200) => `${BASE}/api/audit/export.csv?limit=${limit}`,
}

export const opsApi = {
  adaptersStatus: () => api.get<AdaptersStatus>('/api/adapters/status'),
  metricsText: async (): Promise<string> => {
    const res = await fetch(`${BASE}/metrics`)
    if (!res.ok) throw new ApiError(res.status, res.statusText)
    return res.text()
  },
  precheck: (accountId?: string, signals?: string) => {
    const params = new URLSearchParams()
    if (accountId) params.set('account_id', accountId)
    if (signals) params.set('signals', signals)
    const qs = params.toString()
    return api.get<PrecheckStatus>(`/api/precheck${qs ? '?' + qs : ''}`)
  },
  healthz: () => api.get<Record<string, unknown>>('/healthz'),
}

export const signalsEditorApi = {
  list: () => api.get<{ signals: SignalFileInfo[] }>('/api/strategy/signals-list'),
  content: (path: string) =>
    api.get<SignalsContent>(
      `/api/strategy/signals-content?path=${encodeURIComponent(path)}`,
    ),
  save: (body: { path: string; content: string }) =>
    api.post('/api/strategy/signals-save', {
      path: body.path,
      payload: JSON.parse(body.content) as Record<string, unknown>,
    }),
}

export const shioajiApi = {
  simulationSmoke: () => api.get<ShioajiSmoke>('/api/shioaji/simulation-smoke'),
  sinopacDryRun: () => api.get<ShioajiSmoke>('/api/shioaji/sinopac-dry-run'),
  sinopacApiTest: (body: Record<string, unknown>) =>
    api.post<ShioajiSmoke>('/api/shioaji/sinopac-api-test', body),
}

// ── FinMind market data ───────────────────────────────────────────────────────

/** Market-wide balance + day-over-day change (融資/融券 totals). */
export interface MarginBalance { today: number; yesterday: number; delta: number }
export interface MarginTotals {
  margin_money: MarginBalance | null    // 融資餘額（元）
  margin_shares: MarginBalance | null   // 融資餘額（張）
  short_shares: MarginBalance | null    // 融券餘額（張）
  date: string | null
}

export interface MarketOverview {
  institutional: InstitutionalRow[]
  /** Market-wide net (authoritative, from the dataset's total row). */
  institutional_total?: { buy: number; sell: number; net: number }
  margin: MarginTotals
  us_indices: UsIndexRow[]
  as_of: string
}

export interface InstitutionalRow {
  date: string
  name: string           // 外資/投信/自營商
  buy: number
  sell: number
  buy_deal: number
  sell_deal: number
}

export interface MarginRow {
  date: string
  stock_id: string
  MarginPurchaseBuy: number
  MarginPurchaseSell: number
  MarginPurchaseRedeem: number
  MarginPurchaseCashRepayment: number
  MarginPurchaseBalance: number   // 融資餘額（張）
  ShortSaleBuy: number
  ShortSaleSell: number
  ShortSaleBalance: number        // 融券餘額（張）
}

export interface DividendRow {
  date: string            // ex-dividend date (除息日)
  stock_id: string
  CashEarningsDistribution: number   // 現金股利（元）
  StockEarningsDistribution: number  // 股票股利（元）
  CashStatutoryReserve?: number
  StockStatutoryReserve?: number
  CashExDividendTradingDate?: string
  StockExDividendTradingDate?: string
  CashDividendPaymentDate?: string
  CompanyType?: string
}

export interface UsIndexRow {
  index_id: string
  name?: string
  ticker?: string
  date: string
  open: number
  max?: number
  min?: number
  close: number
  volume?: number
  change_price: number
  change_pct?: number
}

export interface UsIndicesResponse {
  data: UsIndexRow[]       // legacy (overview)
  source: string
}

export interface UsIndicesGrouped {
  groups: { us: UsIndexRow[]; global: UsIndexRow[]; oil: UsIndexRow[] }
  source: string
}

export interface TickerChartResponse {
  ticker: string
  period: string
  ohlcv: Array<{ time: string; open: number; high: number; low: number; close: number; volume: number }>
  source: string
}

export interface OhlcvRow {
  date: string
  open: number
  high: number
  low: number
  close: number
  volume: number
  change: number
}

export interface StockFinancials {
  symbol: string
  eps: Array<{ date: string; type: string; value: number }>
  revenue: Array<{ date: string; revenue: number }>
}

/** TaiwanStockPER daily row — 估值基本面（本益比/淨值比/殖利率）。 */
export interface ValuationRow {
  date: string
  PER: number
  PBR: number
  dividend_yield: number
}

/** 每季獲利能力比率（後端算好，%）；null = 分母缺失，不估算。 */
export interface ProfitRatioQuarter {
  date: string
  revenue: number | null
  gross_margin: number | null
  operating_margin: number | null
  net_margin: number | null
  eps: number | null
}

/** 公司概況：產業類別 + 資本額 + 每季獲利比率。 */
export interface StockProfile {
  symbol: string
  company: { name: string | null; industry: string | null; market: string | null }
  capital_stock: number | null      // 元
  quarters: ProfitRatioQuarter[]
  business_mix_available: boolean    // FinMind 無產品別營收占比 → 恆 false
}

export interface FuturesPosition {
  code: string | null; product_id: string | null; direction: string
  quantity: number; price: number; last_price: number; pnl: number
  margin_purchase_amount: number; yd_quantity: number
}
export interface FuturesContract {
  code: string; name: string; delivery_date: string
  limit_up: number; limit_down: number; reference: number; multiplier: number
}
export interface FuturesOrder {
  code: string | null; name: string | null; action: string
  price: number; quantity: number; filled_quantity: number
  status: string; order_id: string | null; msg: string | null; octype: string
}
export interface FuturesStatus {
  connected: boolean; has_futopt_account: boolean
  has_login_credentials: boolean; live_trading_enabled: boolean
  simulation: boolean; window_open: boolean; window_msg: string; rate_ok: boolean
}
export interface FuturesChartPoint {
  time: string
  open: number
  high: number
  low: number
  close: number
  volume: number
}
export interface FuturesChartResponse {
  product: string
  contract_code: string
  hours: number
  ohlcv: FuturesChartPoint[]
  source: string
  message?: string
}

export const futuresApi = {
  status: () => api.get<FuturesStatus>('/api/futures/status'),
  positions: () => api.get<{ positions: FuturesPosition[]; has_futopt_account: boolean; message?: string }>('/api/futures/positions'),
  contracts: (product = 'TXF') => api.get<{ contracts: FuturesContract[]; product: string }>(`/api/futures/contracts?product=${product}`),
  orders: () => api.get<{ orders: FuturesOrder[] }>('/api/futures/orders'),
  chart: (product = 'TXF', contractCode = 'TXFR1', hours = 12) =>
    api.get<FuturesChartResponse>(
      `/api/futures/chart?product=${encodeURIComponent(product)}&contract_code=${encodeURIComponent(contractCode)}&hours=${hours}`,
    ),
  placeOrder: (body: { contract_code: string; side: string; quantity: number; price: number; octype?: string; product?: string }) =>
    api.post<{ ok: boolean; order_id?: string; status?: string; msg?: string; message?: string }>('/api/futures/place-order', body),
}

// ── Training Management (overview + requeue) ──────────────────────────────────

export interface TrainedSignal {
  symbol: string
  strategy: string
  /** From API: stock vs futures (inferred on server for signal files). */
  asset_type?: 'stock' | 'futures'
  signal: 'long' | 'short' | 'hold' | 'neutral'
  entry_price: number | null
  exit_price: number | null
  trained_at: string
  /** Last trading date of price bars used for training (YYYY-MM-DD). */
  price_data_through?: string | null
  queue_status: 'done' | 'pending'
  signal_file: string
  metadata?: Record<string, unknown>
}

export interface PendingQueueItem {
  symbol: string
  strategy: string
  added_at: string
  status: string
}

export interface BacktestSummary {
  run_date: string | null
  sharpe: number | null
  max_drawdown_pct: number | null
  total_return_pct: number | null
  cagr_pct: number | null
  win_rate: number | null
  trade_count: number | null
}

export interface WatchlistSymbolStatus {
  symbol: string
  strategy_kind: string
  asset_type: string
  enabled: boolean
  has_signals: boolean
  queue_status: 'done' | 'pending' | 'untrained'
  backtest: BacktestSummary | null
}

/** Server-computed freshness aggregate (single source of truth; never recompute). */
export interface SignalCounts {
  /** enabled items whose signal is up-to-date */
  fresh: number
  /** enabled items whose signal is stale / legacy (needs retrain) */
  stale: number
  /** enabled items with no signal file at all */
  missing: number
  /** True when price_dataset has no dates (DB down) → counts unreliable */
  unknown_price: boolean
}

export interface TrainingOverview {
  trained: TrainedSignal[]
  pending: PendingQueueItem[]
  training_active: boolean
  training_symbol: string | null
  watchlist_symbols: WatchlistSymbolStatus[]
  /** Global MAX(date) across price_dataset (deprecated; use latest_price_dates). */
  latest_price_date: string | null
  /** Per-symbol MAX(date) in price_dataset for staleness checks. */
  latest_price_dates: Record<string, string>
  /** ✅fresh / ⏳stale / ❌missing computed server-side (enabled-only basis). */
  signal_counts: SignalCounts
}

export interface TrainingSymbolResults {
  symbol: string
  strategies: TrainedSignal[]
  queue_status: 'done' | 'pending' | 'untrained'
  last_trained: string | null
}

export interface TrainingLogsResponse {
  lines: string[]
  log_file: string | null       // basename of log file
  log_path?: string | null      // full path (from richer training.py handler)
  run_id?: string
  status?: string
  line_count?: number
  source?: string
}

export interface SystemStatus {
  uptime_seconds: number
  uptime_human: string
  pid: number | null
  python_version: string
  psutil_available: boolean
  memory: { used_mb: number; total_mb: number; percent: number } | null
  cpu_percent: number | null
  disk: { used_gb: number; total_gb: number; percent: number } | null
}

export const trainingMgmtApi = {
  overview: () => api.get<TrainingOverview>('/api/training/overview'),
  results: (symbol: string) =>
    api.get<TrainingSymbolResults>(`/api/training/results/${encodeURIComponent(symbol)}`),
  logs: (n = 50) => api.get<TrainingLogsResponse>(`/api/training/logs?n=${n}`),
  requeue: (symbol: string, strategy: string) =>
    api.post<{ ok: boolean; symbol: string; strategy: string }>('/api/training/requeue', { symbol, strategy }),
  requeueAll: () =>
    api.post<{ ok: boolean; queued: number; message?: string }>('/api/training/requeue-all'),
}

export interface PipelineRunReport {
  started_at?: string
  finished_at?: string
  date?: string
  dry_run?: boolean
  never_run?: boolean
  steps?: {
    check?: { ok: boolean; watchlist_count?: number; signals?: { ok: number; stale: number; missing: number }; prices?: { csv_exists: boolean; needs_update: number } }
    prices?: { ok: boolean; skipped?: boolean; log_tail?: string }
    train?: { ok: boolean; skipped?: boolean; log_tail?: string; reason?: string }
  }
  summary?: {
    overall_ok: boolean
    watchlist_symbols: number
    signals_ok_before: number
    signals_missing_before: number
    signals_stale_before: number
    prices_updated: boolean
    training_ran: boolean
  }
}

export interface PipelineHealth {
  /** 'running' = a run started today and has not finished; healthy, not an anomaly. */
  status: 'ok' | 'running' | 'warning' | 'error'
  message: string
  days_since: number | null
}

/** One completed run, kept after the next run overwrites last_run. */
export interface PipelineRunHistoryEntry {
  date: string | null
  started_at: string | null
  finished_at: string | null
  ok: boolean
  failed_steps: string[]
}

export interface PipelineStatus {
  last_run: PipelineRunReport
  next_run: string
  schedule: string
  health: PipelineHealth
  /** Newest first. Absent on an older backend — treat undefined as unknown, not empty. */
  recent_runs?: PipelineRunHistoryEntry[]
}

export interface BackfillProgress {
  progress: {
    cursor: number
    universe_size: number
    pct_done: number
    rounds_done: number
    days_to_finish: number
    last_run: string | null
    target_days: number
    batch_size: number
  }
  db_coverage: {
    buckets: Record<string, number>
    median_bars: number
    total_tics: number
  }
  schedule: string
}

export interface PriceCoverage {
  bar_count: number
  date_from: string | null
  date_to: string | null
  sufficient: boolean
}

export const systemApi = {
  status: () => api.get<SystemStatus>('/api/system/status'),
  pipeline: () => api.get<PipelineStatus>('/api/system/pipeline'),
  backfillProgress: () => api.get<BackfillProgress>('/api/system/backfill-progress'),
  priceCoverage: (symbols: string[]) =>
    api.get<{ coverage: Record<string, PriceCoverage> }>(
      `/api/system/price-coverage?symbols=${symbols.map(encodeURIComponent).join(',')}`
    ),
}

// ── Data pipeline health & unified update ────────────────────────────────────

export interface DataHealthItem {
  symbol: string
  strategy_kind: string
  asset_type: string
  enabled: boolean
  price: { status: 'ok' | 'stale' | 'missing' | 'unknown'; latest_date: string | null }
  signal: { status: 'ok' | 'stale' | 'missing' | 'unknown'; generated_at: string | null }
  ready: boolean
}

export interface DataHealthStatus {
  /** 新鮮度基準＝最近一個已收盤的台股交易日；null = 日曆不可用、無法確認。 */
  basis_date: string | null
  items: DataHealthItem[]
  summary: {
    total: number
    price_ok: number
    price_stale: number
    price_missing: number
    signal_ok: number
    signal_stale: number
    signal_missing: number
    all_ready: boolean
  }
}

export interface PipelineRunResult {
  ok?: boolean
  message?: string
  cmd?: string
  log?: string
  error?: string
}

export const pipelineApi = {
  status: () => api.get<DataHealthStatus>('/api/pipeline/status'),
  run: (opts?: { force?: boolean; skip_prices?: boolean; skip_train?: boolean }) =>
    api.post<PipelineRunResult>('/api/pipeline/run', opts ?? {}),
  lastRun: () => api.get<Record<string, unknown>>('/api/pipeline/last-run'),
}

export interface KbarRow {
  date: string
  open: number
  high: number
  low: number
  close: number
  volume: number
  tic: string
}

export const marketApi = {
  overview: () => api.get<MarketOverview>('/api/market/overview'),
  usIndices: () => api.get<UsIndicesGrouped>('/api/market/us-indices'),
  tickerChart: (ticker: string, period = '3mo') =>
    api.get<TickerChartResponse>(`/api/market/ticker-chart?ticker=${encodeURIComponent(ticker)}&period=${period}`),
  /** Fetch OHLCV kbars from Shioaji directly (VM must be logged in). */
  kbars: (symbol: string, start: string, end: string, assetType = 'stock') =>
    request<{ rows: KbarRow[] }>('/api/market/kbars', {
      method: 'POST',
      body: JSON.stringify({ symbol, start, end, asset_type: assetType }),
    }),
  /** Shioaji snapshot: today's price + exchange-computed change_rate (%). Returns null on 503/404. */
  snapshot: (symbol: string) =>
    request<{ symbol: string; price: number; open: number; high: number; low: number; change_price: number; change_rate: number; volume: number; source: string }>(
      `/api/market/snapshot/${encodeURIComponent(symbol)}`
    ).catch(() => null),
  stockPrice: (symbol: string) =>
    api.get<{ symbol: string; ohlcv: OhlcvRow[] }>(`/api/market/stock/${symbol}`),
  stockInstitutional: (symbol: string) =>
    api.get<{ symbol: string; data: InstitutionalRow[] }>(`/api/market/stock/${symbol}/institutional`),
  stockMargin: (symbol: string) =>
    api.get<{ symbol: string; data: MarginRow[] }>(`/api/market/stock/${symbol}/margin`),
  stockFinancials: (symbol: string) =>
    api.get<StockFinancials>(`/api/market/stock/${symbol}/financials`),
  stockDividend: (symbol: string) =>
    api.get<{ symbol: string; data: DividendRow[] }>(`/api/market/stock/${symbol}/dividend`),
  stockValuation: (symbol: string) =>
    api.get<{ symbol: string; data: ValuationRow[] }>(`/api/market/stock/${symbol}/valuation`),
  stockProfile: (symbol: string) =>
    api.get<StockProfile>(`/api/market/stock/${symbol}/profile`),
}

export const experimentApi = {
  matrix: () => api.get<ExperimentMatrix>('/api/experiment/matrix'),
}
